import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

/*
	Globito del Calendario: se abre con el ratón encima (220 ms) o con clic. Con clic queda fijo hasta otro clic, Escape o un
	clic afuera; con el ratón se cierra al salir del botón y del globito. Va en un portal con posición fija para que no lo
	recorten las tarjetas ni el scroll de la semana.
*/

interface Abierto { llave: string; ancla: HTMLElement; contenido: React.ReactNode; fijo: boolean; }

export interface GlobitoApi {
	/** Props para el disparador: onMouseEnter/Leave y onClick. */
	disparador: (llave: string, contenido: () => React.ReactNode) => {
		onMouseEnter: (e: React.MouseEvent<HTMLElement>) => void;
		onMouseLeave: () => void;
		onClick: (e: React.MouseEvent<HTMLElement>) => void;
		"aria-expanded": boolean;
	};
	cerrar: () => void;
	capa: React.ReactNode;
}

export const useGlobito = (): GlobitoApi => {
	const [abierto, setAbierto] = useState<Abierto | null>(null);
	const relojAbrir = useRef<number | undefined>(undefined);
	const relojCerrar = useRef<number | undefined>(undefined);
	const caja = useRef<HTMLDivElement | null>(null);
	const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number } | null>(null);

	const cerrar = useCallback(() => { window.clearTimeout(relojAbrir.current); window.clearTimeout(relojCerrar.current); setAbierto(null); setPos(null); }, []);

	useEffect(() => {
		if (!abierto) return;
		const fuera = (e: MouseEvent) => {
			if (caja.current?.contains(e.target as Node) || abierto.ancla.contains(e.target as Node)) return;
			cerrar();
		};
		const tecla = (e: KeyboardEvent) => { if (e.key === "Escape") cerrar(); };
		// Desplazarse DENTRO del globito (una lista larga) no lo cierra.
		const scroll = (e: Event) => { if (!abierto.fijo && !caja.current?.contains(e.target as Node)) cerrar(); };
		document.addEventListener("mousedown", fuera);
		document.addEventListener("keydown", tecla);
		window.addEventListener("scroll", scroll, true);
		return () => { document.removeEventListener("mousedown", fuera); document.removeEventListener("keydown", tecla); window.removeEventListener("scroll", scroll, true); };
	}, [abierto, cerrar]);

	useEffect(() => () => { window.clearTimeout(relojAbrir.current); window.clearTimeout(relojCerrar.current); }, []);

	// Se mide ya pintado: abajo del botón, o arriba si no cabe; nunca fuera de la pantalla.
	// Se vuelve a medir cuando llega la lista y el globito crece ("Cargando…" → nombres).
	useLayoutEffect(() => {
		const el = caja.current;
		if (!abierto || !el) return;
		/*
			Abajo del botón si cabe; si no, arriba; si la lista no cabe en ninguno de los dos lados, del lado con más espacio y con
			scroll adentro. Nunca se sale de la pantalla ni tapa su botón (antes, abajo en la pantalla, quedaba 8 px afuera).
		*/
		const medir = () => {
			const r = abierto.ancla.getBoundingClientRect();
			const w = el.offsetWidth, natural = el.scrollHeight;
			const left = Math.min(Math.max(8, r.left), window.innerWidth - w - 8);
			const abajo = window.innerHeight - r.bottom - 14, arriba = r.top - 14;
			const alto = Math.min(natural, 380);
			let top: number, maxHeight: number;
			if (alto <= abajo) { top = r.bottom + 6; maxHeight = abajo; }
			else if (alto <= arriba) { top = r.top - 6 - alto; maxHeight = arriba; }
			else if (abajo >= arriba) { top = r.bottom + 6; maxHeight = Math.max(120, abajo); }
			else { maxHeight = Math.max(120, arriba); top = Math.max(8, r.top - 6 - maxHeight); }
			setPos((p) => (p && p.left === left && p.top === top && p.maxHeight === maxHeight ? p : { left, top, maxHeight }));
		};
		medir();
		const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(medir) : null;
		ro?.observe(el);
		// Cuando llega la lista cambia el contenido: se mide en ese momento, sin esperar al siguiente cuadro.
		const mo = new MutationObserver(medir);
		mo.observe(el, { childList: true, subtree: true, characterData: true });
		return () => { ro?.disconnect(); mo.disconnect(); };
	}, [abierto]);

	const disparador: GlobitoApi["disparador"] = (llave, contenido) => ({
		onMouseEnter: (e) => {
			const ancla = e.currentTarget;
			window.clearTimeout(relojCerrar.current);
			if (abierto?.fijo || abierto?.llave === llave) return;
			window.clearTimeout(relojAbrir.current);
			relojAbrir.current = window.setTimeout(() => setAbierto({ llave, ancla, contenido: contenido(), fijo: false }), 220);
		},
		onMouseLeave: () => {
			window.clearTimeout(relojAbrir.current);
			if (!abierto?.fijo) relojCerrar.current = window.setTimeout(() => { setAbierto(null); setPos(null); }, 180);
		},
		onClick: (e) => {
			e.stopPropagation();
			window.clearTimeout(relojAbrir.current);
			if (abierto?.llave === llave && abierto.fijo) { cerrar(); return; }
			setPos(null);
			setAbierto({ llave, ancla: e.currentTarget, contenido: contenido(), fijo: true });
		},
		"aria-expanded": abierto?.llave === llave,
	});

	const capa = abierto ? ReactDOM.createPortal(
		<div ref={caja} className="calcGlobo" role="dialog" style={pos ? { left: pos.left, top: pos.top, maxHeight: Math.min(380, pos.maxHeight) } : { left: -9999, top: 0 }}
			onClick={(e) => e.stopPropagation()}
			onMouseEnter={() => window.clearTimeout(relojCerrar.current)}
			onMouseLeave={() => { if (!abierto.fijo) relojCerrar.current = window.setTimeout(() => { setAbierto(null); setPos(null); }, 180); }}>
			{abierto.contenido}
		</div>, document.body) : null;

	return { disparador, cerrar, capa };
};
